import {
  deleteV1CompaniesCompanyIdRatesId,
  getV1CompaniesCompanyIdRates,
  getV1CompaniesCompanyIdRatesId,
  getV1CompaniesCompanyIdRatesTypeahead,
  patchV1CompaniesCompanyIdRatesId,
  postV1CompaniesCompanyIdRates,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { Rate } from '~hooks/useRates/models';
import { RatesFormSchemaInterface } from '~pages/Settings/RatesManagement/rateFormValidationSchema';
import { rateFilterUsingOwnerTypeOptions } from '~pages/Settings/RatesManagement/rateUtils';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink, PaginationQuery } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface CommonRateParams {
  companyId?: string;
  id: string;
}

interface UpdateRateParams {
  companyId?: string;
  rate: RatesFormSchemaInterface;
  ownerType: RateOwnerType;
}
interface CreateRateParams {
  companyId?: string;
  rate: RatesFormSchemaInterface;
  ownerType: RateOwnerType;
}

interface GetAccountRatesUsingOwnerTypeParams {
  accountId?: string;
  driverId?: string;
  equipmentId?: string;
  includeDefaultRates?: boolean;
  link?: PaginationLink;
  ownerType?: RateOwnerType;
  projectId?: string;
  serviceId?: string;
  cursor?: string;
  query?: string;
  limit?: number;
  companyId: string;
}

export const useRates = () => {
  const [isLoadingRates, setIsLoadingRates] = useState<boolean>(false);
  const [isUpdatingRate, setIsUpdatingRate] = useState<boolean>(false);
  const { companyAssetsStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const getAccountRatesUsingOwnerType = async ({
    accountId,
    driverId,
    equipmentId,
    includeDefaultRates,
    link,
    ownerType,
    projectId,
    serviceId,
    cursor,
    query,
    limit,
    companyId,
  }: GetAccountRatesUsingOwnerTypeParams) => {
    const params: PaginationQuery = {
      'page[limit]': limit || companyAssetsStore.ratesPagination.limit,
      [rateFilterUsingOwnerTypeOptions.account]: accountId,
      [rateFilterUsingOwnerTypeOptions.driverId]: driverId || '',
      [rateFilterUsingOwnerTypeOptions.equipment]: equipmentId || '',
      [rateFilterUsingOwnerTypeOptions.includeDefaultRates]: includeDefaultRates || false,
      [rateFilterUsingOwnerTypeOptions.ownerType]: ownerType || '',
      [rateFilterUsingOwnerTypeOptions.project]: projectId || '',
      [rateFilterUsingOwnerTypeOptions.service]: serviceId || '',
    };

    if (cursor) {
      params['page[after]'] = cursor;
    } else if (link && companyAssetsStore.ratesPagination[link]) {
      params[`page[${link}]`] = companyAssetsStore.ratesPagination[link];
    }

    const isTypeahead = typeof query === 'string' && query.length > 0;
    const queryParams: PaginationQuery = { ...params };

    if (isTypeahead) {
      queryParams[rateFilterUsingOwnerTypeOptions.query] = query;
    }

    setIsLoadingRates(true);

    try {
      const response = isTypeahead
        ? await getV1CompaniesCompanyIdRatesTypeahead({
            path: { 'company-id': companyId },
            query: queryParams,
          })
        : await getV1CompaniesCompanyIdRates({
            path: { 'company-id': companyId },
            query: queryParams,
          });

      const formatted = response.data.data.map(Rate.parse);
      const pagination = extractPagination(response);

      return { data: formatted, pagination };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.rates.failed_to_fetch'));
    } finally {
      setIsLoadingRates(false);
    }
  };

  const getOneRate = async ({ companyId: rateCompanyId, id }: CommonRateParams) => {
    setIsLoadingRates(true);

    try {
      const response = await getV1CompaniesCompanyIdRatesId({
        path: { 'company-id': rateCompanyId || companyId, id },
      });
      const fetchedRate = Rate.parse(response.data.data);

      companyAssetsStore.addRate(fetchedRate);
      return fetchedRate;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.rates.failed_to_fetch_rate'),
      );
    } finally {
      setIsLoadingRates(false);
    }
  };

  const deleteRate = async ({ id }: CommonRateParams) => {
    setIsLoadingRates(true);

    try {
      await deleteV1CompaniesCompanyIdRatesId({
        path: { 'company-id': companyId, id },
      });
      companyAssetsStore.deleteRate(id);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.rates.failed_to_delete'));
    } finally {
      setIsLoadingRates(false);
    }
  };

  const updateRate = async ({
    companyId: formCompanyId,
    rate,
    ownerType,
  }: UpdateRateParams) => {
    setIsUpdatingRate(true);

    try {
      const response = await patchV1CompaniesCompanyIdRatesId({
        path: {
          'company-id': formCompanyId || companyId,
          id: String(rate?.id),
        },
        body: Rate.deparseUpdate(rate, ownerType),
      });
      const updatedRate = Rate.parse(response.data.data);

      companyAssetsStore.updateRate(updatedRate);
      return updatedRate;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.rates.failed_to_update'));
    } finally {
      setIsUpdatingRate(false);
    }
  };
  const createRate = async ({
    companyId: formCompanyId,
    rate,
    ownerType,
  }: CreateRateParams) => {
    setIsUpdatingRate(true);

    const rateToCreate = Rate.deparseCreate(rate, ownerType);

    try {
      const response = await postV1CompaniesCompanyIdRates({
        path: { 'company-id': formCompanyId || companyId },
        body: rateToCreate,
      });
      const createdRate = Rate.parse(response.data.data);

      companyAssetsStore.addRate(createdRate);

      return createdRate;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.rates.failed_to_create'));
    } finally {
      setIsUpdatingRate(false);
    }
  };

  return {
    createRate,
    deleteRate,
    getAccountRatesUsingOwnerType,
    getOneRate,
    isLoadingRates,
    isUpdatingRate,
    updateRate,
  };
};
